import React, { useState } from 'react';
import { ArrowRight, Battery, Zap, Train, Check, ChevronRight, Home, Factory, Truck, Plane, Shield } from 'lucide-react';

const ProductShowcase = () => {
  const [activeProduct, setActiveProduct] = useState(0);

  // Six application domains covering the full product platform
  const products = [
    {
      id: 'residential-ess',
      name: 'Residential ESS',
      tagline: 'Home & Small-Scale',
      chemistry: 'Multi-Ion',
      capacity: '12V–48V / 50–200Ah',
      icon: Home,
      video: 'https://videos.pexels.com/video-files/4324109/4324109-uhd_2560_1440_24fps.mp4',
      videoPoster: 'https://customer-assets.emergentagent.com/job_ea8b3b14-e3e6-436d-8ca4-049dd423dd0c/artifacts/a81kd2tj_image.png',
    },
    {
      id: 'industrial-ess',
      name: 'Industrial ESS',
      tagline: 'Infrastructure Storage',
      chemistry: 'Multi-Ion',
      capacity: '48V–400V / 100Ah–1500Ah',
      icon: Zap,
      video: 'https://videos.pexels.com/video-files/30783649/13167458_2560_1440_60fps.mp4',
      videoPoster: 'https://customer-assets.emergentagent.com/job_ea8b3b14-e3e6-436d-8ca4-049dd423dd0c/artifacts/qrsm4x8d_image.png',
    },
    {
      id: 'traction-mobility',
      name: 'Traction & Mobility',
      tagline: 'Industrial Vehicles',
      chemistry: 'Multi-Ion',
      capacity: '24V–80V / 200–1500Ah',
      icon: Battery,
      video: 'https://videos.pexels.com/video-files/5115941/5115941-uhd_2560_1440_30fps.mp4',
      videoPoster: 'https://customer-assets.emergentagent.com/job_ea8b3b14-e3e6-436d-8ca4-049dd423dd0c/artifacts/z6ktnmd5_Screenshot%202026-02-04%20at%2002.21.04.png',
    },
    {
      id: 'heavy-equipment',
      name: 'Heavy Equipment',
      tagline: 'Material Handling',
      chemistry: 'Multi-Ion',
      capacity: '48V–400V / 500–1500Ah',
      icon: Truck,
      video: 'https://videos.pexels.com/video-files/32386621/13814766_2560_1440_100fps.mp4',
      videoPoster: 'https://images.pexels.com/videos/32386621/pexels-photo-32386621.jpeg?auto=compress&cs=tinysrgb&w=1920',
    },
    {
      id: 'ground-power',
      name: 'Ground Power',
      tagline: 'Civil Aviation & Industrial',
      chemistry: 'Multi-Ion',
      capacity: '28V–400V / 200–1500Ah',
      icon: Plane,
      video: 'https://videos.pexels.com/video-files/1635382/1635382-hd_1920_1080_30fps.mp4',
      videoPoster: 'https://images.pexels.com/videos/1635382/free-video-1635382.jpg?auto=compress&cs=tinysrgb&w=1920',
    },
    {
      id: 'defense-power',
      name: 'Defense Systems',
      tagline: 'Military Ground Power',
      chemistry: 'Multi-Ion',
      capacity: '24V–400V / 100–1500Ah',
      icon: Shield,
      video: 'https://videos.pexels.com/video-files/12122644/12122644-uhd_2560_1440_25fps.mp4',
      videoPoster: 'https://images.pexels.com/videos/12122644/military-vehicle-ukraine-12122644.jpeg?auto=compress&cs=tinysrgb&w=1920',
    },
  ];

  // Detailed specifications for each application domain
  const productDetails = {
    'residential-ess': {
      title: 'Residential & Small-Scale Energy Storage',
      subtitle: 'Home Backup & Small Commercial Power',
      category: 'Energy Storage Systems',
      seoTitle: 'Home Battery Backup - Residential Energy Storage System',
      voltageRange: '12V – 48V',
      capacityRange: '50Ah – 200Ah',
      primaryRequirement: 'Safety, Long Service Life, Thermal Stability',
      description: 'Battery systems designed for home backup power and small commercial installations. Unlike consumer-grade solutions, these systems prioritize inherent safety and long service life over maximum energy density. Suitable for backup power during outages, load shifting, and small-scale off-grid applications where fire safety in enclosed spaces is non-negotiable.',
      impact: 'Deployed in residential backup applications where the battery operates in proximity to living spaces. The inherent safety profile makes it suitable for indoor installation without supplementary fire suppression systems.',
      specs: [
        { label: 'Voltage Range', value: '12V – 48V' },
        { label: 'Capacity', value: '50Ah – 200Ah' },
        { label: 'Cycle Life', value: '5,000+ cycles' },
        { label: 'Charge Time', value: '< 2 hours' },
        { label: 'Temp Range', value: '-20°C to +55°C' },
        { label: 'Form Factor', value: 'Modules / Racks' },
      ],
      features: [
        'Inherently safe chemistry for indoor installation',
        'Long calendar life with minimal capacity degradation',
        'Compatible with standard inverter systems',
        'Passive thermal management',
      ],
      applications: ['Home Backup', 'Small Commercial', 'Off-Grid Cabins', 'Emergency Power'],
    },
    'industrial-ess': {
      title: 'Industrial & Infrastructure ESS',
      subtitle: 'Factory, Data Center & Grid-Edge Storage',
      category: 'Industrial Energy Storage',
      seoTitle: 'Industrial Energy Storage System - Data Center & Microgrid Battery',
      voltageRange: '48V – 400V',
      capacityRange: '100Ah – 1500Ah',
      primaryRequirement: 'Reliability, Fire Safety, High Cycle Durability',
      description: 'Scalable energy storage systems for industrial facilities, data centers, microgrids, and grid-edge deployments. Engineered for high cycle count operation, deep discharge tolerance, and continuous duty in confined or temperature-controlled environments. These systems support factory backup, peak shaving, demand response, and critical infrastructure continuity.',
      impact: 'Deployed in mission-critical environments where unplanned downtime carries significant financial or operational consequences. The inherent fire safety of Multi-Ion chemistry reduces insurance requirements and eliminates the need for dedicated suppression systems in server rooms and industrial facilities.',
      specs: [
        { label: 'Voltage Range', value: '48V – 400V DC' },
        { label: 'Capacity', value: '100Ah – 1500Ah' },
        { label: 'Cycle Life', value: '10,000+ cycles' },
        { label: 'Efficiency', value: '>95% round-trip' },
        { label: 'Temp Range', value: '-40°C to +65°C' },
        { label: 'Form Factor', value: 'Racks / Containers' },
      ],
      features: [
        'Modular scaling from 50kWh to multi-MWh',
        'SCADA and BMS integration for facility management',
        'Passive thermal management—no chiller systems required',
        'Compliant with UL 9540A fire safety testing',
      ],
      applications: ['Data Centers', 'Factory Backup', 'Microgrids', 'Grid-Edge Storage'],
    },
    'traction-mobility': {
      title: 'Industrial Traction & Mobility',
      subtitle: 'Forklifts, AGVs & Warehouse Equipment',
      category: 'Traction Battery Systems',
      seoTitle: 'Forklift Battery - Industrial Traction & AGV Battery Systems',
      voltageRange: '24V – 80V',
      capacityRange: '200Ah – 1500Ah',
      primaryRequirement: 'Fast Charging, High Cycle Life, Maximum Uptime',
      description: 'Traction battery systems for Class I–III forklifts, automated guided vehicles (AGVs), tow tractors, and warehouse mobility equipment. Designed as drop-in replacements for lead-acid batteries with identical form factors. The 15-minute fast charge capability enables opportunity charging during breaks, eliminating the need for battery swap stations and spare inventory.',
      impact: 'Operations teams report eliminating second-shift battery swaps entirely. A single Multi-Ion battery can replace 2–3 lead-acid units in rotating fleets, reducing capital expenditure and warehouse footprint dedicated to battery infrastructure.',
      specs: [
        { label: 'Voltage Range', value: '24V / 48V / 80V' },
        { label: 'Capacity', value: '200Ah – 1500Ah' },
        { label: 'Cycle Life', value: '5,000+ cycles' },
        { label: 'Fast Charge', value: '15 minutes (0–80%)' },
        { label: 'Temp Range', value: '-40°C to +65°C' },
        { label: 'Form Factor', value: 'DIN / BCI standard' },
      ],
      features: [
        'Opportunity charging during 15-minute breaks',
        'Drop-in replacement for lead-acid (same dimensions)',
        'Full power delivery at -40°C without preheating',
        'Cloud-connected BMS for fleet health monitoring',
      ],
      applications: ['Forklifts', 'AGVs', 'Tow Tractors', 'Pallet Jacks', 'Order Pickers'],
    },
    'heavy-equipment': {
      title: 'Heavy Equipment & Material Handling',
      subtitle: 'Cranes, Port Equipment & Industrial Vehicles',
      category: 'Heavy-Duty Industrial Power',
      seoTitle: 'Crane Battery - Heavy Equipment & Port Material Handling',
      voltageRange: '48V – 400V',
      capacityRange: '500Ah – 1500Ah',
      primaryRequirement: 'High Current Delivery, Mechanical Robustness, Safety',
      description: 'High-capacity battery systems for cranes, reach stackers, RTG cranes, heavy-duty industrial vehicles, and port material handling equipment. Engineered to deliver sustained high-current output under demanding mechanical conditions. These systems withstand the vibration profiles and shock loads typical of heavy industrial environments while maintaining consistent performance.',
      impact: 'Deployed in port terminals and heavy manufacturing where equipment operates continuously across multiple shifts. The fast-charge capability and extended cycle life reduce the total number of batteries required in a fleet and eliminate the need for dedicated charging facilities.',
      specs: [
        { label: 'Voltage Range', value: '48V – 400V' },
        { label: 'Capacity', value: '500Ah – 1500Ah' },
        { label: 'Cycle Life', value: '5,000+ cycles' },
        { label: 'Peak Current', value: 'Up to 3C continuous' },
        { label: 'Temp Range', value: '-40°C to +65°C' },
        { label: 'Form Factor', value: 'Custom enclosures' },
      ],
      features: [
        'Regenerative braking energy capture',
        'Vibration and shock resistant construction',
        'Field-serviceable modular architecture',
        'Inherently safe chemistry for industrial environments',
      ],
      applications: ['Container Cranes', 'Reach Stackers', 'RTG Cranes', 'Heavy Forklifts', 'Mining Vehicles'],
    },
    'ground-power': {
      title: 'Ground Power Systems',
      subtitle: 'Civil Aviation GPU & Industrial Ground Power',
      category: 'Ground Power Units',
      seoTitle: 'Ground Power Unit Battery - Airport GPU & Industrial Power',
      voltageRange: '28V – 400V',
      capacityRange: '200Ah – 1500Ah',
      primaryRequirement: 'Stable Output, Continuous Duty, Operational Safety',
      description: 'Battery-based ground power units (GPUs) for civil aviation and industrial applications. These systems provide stable DC and AC power output for aircraft servicing, temporary power installations, and mobile power requirements. Designed for continuous duty operation with consistent voltage regulation under varying load conditions.',
      impact: 'Used in airport ground support operations where the GPU must provide clean, stable power to aircraft systems during turnaround. The elimination of diesel generators reduces noise, emissions, and fuel logistics at terminal gates while meeting strict aviation safety requirements.',
      specs: [
        { label: 'Voltage Range', value: '28V DC / 115V AC / 400V' },
        { label: 'Capacity', value: '200Ah – 1500Ah' },
        { label: 'Cycle Life', value: '5,000+ cycles' },
        { label: 'Output Stability', value: '±1% regulation' },
        { label: 'Temp Range', value: '-40°C to +55°C' },
        { label: 'Form Factor', value: 'Mobile / Fixed units' },
      ],
      features: [
        'Clean power output meeting aviation standards',
        'Silent operation—no diesel generator noise',
        'Rapid recharge between aircraft turnarounds',
        'Suitable for hangar and ramp-side deployment',
      ],
      applications: ['Airport GPUs', 'Aircraft Servicing', 'Mobile Power', 'Temporary Installations'],
    },
    'defense-power': {
      title: 'Defense & Military Ground Power',
      subtitle: 'Base Power, Mobile Command & Tactical Systems',
      category: 'Defense Power Systems',
      seoTitle: 'Military Battery System - Defense Ground Power & Base Storage',
      voltageRange: '24V – 400V',
      capacityRange: '100Ah – 1500Ah',
      primaryRequirement: 'Reliability, Thermal Safety, Long Service Life',
      description: 'Battery systems for defense applications including base power, mobile command units, tactical ground power, and field deployable energy storage. Designed to operate reliably in austere conditions without climate-controlled infrastructure. These systems prioritize long service life, thermal safety under abuse conditions, and consistent performance across extreme temperature ranges.',
      impact: 'Deployed in forward operating bases and field installations where grid power is unavailable and generator fuel logistics are constrained. The inherent safety of Multi-Ion chemistry is suitable for enclosed command vehicles and containerized power systems.',
      specs: [
        { label: 'Voltage Range', value: '24V – 400V' },
        { label: 'Capacity', value: '100Ah – 1500Ah' },
        { label: 'Cycle Life', value: '5,000+ cycles' },
        { label: 'Service Life', value: '10+ years' },
        { label: 'Temp Range', value: '-40°C to +65°C' },
        { label: 'Form Factor', value: 'Ruggedized enclosures' },
      ],
      features: [
        'Full performance at -40°C without thermal conditioning',
        'Inherently safe chemistry for enclosed spaces',
        'Vibration and shock resistant per MIL-STD profiles',
        'Field-serviceable with modular replacement',
      ],
      applications: ['Base Power', 'Mobile Command', 'Tactical Ground Power', 'Field Energy Storage'],
    },
  };

  const activeDetails = productDetails[products[activeProduct].id];

  return (
    <section id="products" className="relative py-20 lg:py-28 bg-gray-950 overflow-hidden" data-testid="products-section">
      {/* Background Elements */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
        <div className="absolute right-0 top-1/4 w-96 h-96 bg-emerald-500/5 blur-[150px] rounded-full" />
        <div className="absolute left-0 bottom-1/4 w-96 h-96 bg-cyan-500/5 blur-[150px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Transition */}
        <div className="text-center mb-8">
          <p className="text-gray-500 text-sm max-w-3xl mx-auto">
            The performance data above applies across our complete platform. Battery systems are configurable as cells, modules, racks, or complete packs to match specific application requirements.
          </p>
        </div>
        
        {/* Section Header - Platform Capability Statement */}
        <header className="text-center mb-12">
          <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">Platform Battery Systems</p>
          <h2 id="products-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Application-Specific Configurations
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto mb-2">
            <span className="text-emerald-400 font-semibold">12V – 400V</span> voltage range | <span className="text-emerald-400 font-semibold">50Ah – 1500Ah</span> capacity range
          </p>
          <p className="text-gray-400 text-base max-w-3xl mx-auto">
            Six application domains spanning residential backup to defense ground power. Each configuration is engineered for its specific operational requirements—whether that's cycle life for industrial traction, thermal safety for indoor storage, or reliability for mission-critical infrastructure.
          </p>
        </header>

        {/* Application Category Overview - 6 Domains in 2 rows - WHITE BACKGROUND */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 mb-8 sm:mb-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" role="list" aria-label="Battery application domains">
            {/* 1. Residential & Small-Scale */}
            <article className="bg-gray-100 border border-gray-200 rounded-lg overflow-hidden" role="listitem">
              <div className="aspect-[16/9] overflow-hidden bg-white">
                <img 
                  src="/images/ess-residential.png" 
                  alt="Residential energy storage system for home backup power in India"
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="225"
                />
              </div>
              <div className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Home className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" aria-hidden="true" />
                  <h3 className="text-gray-900 font-semibold text-xs sm:text-sm uppercase tracking-wider">Residential & Small-Scale</h3>
                </div>
                <p className="text-gray-500 text-[10px] sm:text-xs mb-2">12V–48V | 50–200Ah | Safety & Long Life</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                  Home backup and small commercial energy storage. Prioritizes inherent fire safety for indoor installation.
                </p>
              </div>
            </article>
          
            {/* 2. Industrial & Infrastructure ESS */}
            <article className="bg-gray-100 border border-gray-200 rounded-lg overflow-hidden" role="listitem">
              <div className="aspect-[16/9] overflow-hidden bg-white">
                <img 
                  src="/images/ess-industrial.png" 
                  alt="Industrial energy storage system ESS for commercial and grid applications"
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="225"
                />
              </div>
              <div className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" aria-hidden="true" />
                  <h3 className="text-gray-900 font-semibold text-xs sm:text-sm uppercase tracking-wider">Industrial ESS</h3>
                </div>
                <p className="text-gray-500 text-[10px] sm:text-xs mb-2">48V–400V | 100–1500Ah | Reliability & Fire Safety</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                  Data centers, factory backup, microgrids, and grid-edge storage. High cycle count, passive thermal management.
                </p>
              </div>
            </article>
          
          {/* 3. Industrial Traction & Mobility */}
            {/* 3. Traction & Mobility */}
            <article className="bg-gray-100 border border-gray-200 rounded-lg overflow-hidden" role="listitem">
              <div className="aspect-[16/9] overflow-hidden bg-white">
                <img 
                  src="/images/traction-battery.png" 
                  alt="Traction battery for forklift and warehouse equipment in India"
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="225"
                />
              </div>
              <div className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Battery className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" aria-hidden="true" />
                  <h3 className="text-gray-900 font-semibold text-xs sm:text-sm uppercase tracking-wider">Traction & Mobility</h3>
                </div>
                <p className="text-gray-500 text-[10px] sm:text-xs mb-2">24V–80V | 200–1500Ah | Fast Charge & Uptime</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                  Forklifts, AGVs, tow tractors, and warehouse equipment. 15-minute fast charge eliminates battery swaps.
                </p>
              </div>
            </article>
          
            {/* 4. Heavy Equipment & Material Handling */}
            <article className="bg-gray-100 border border-gray-200 rounded-lg overflow-hidden" role="listitem">
              <div className="aspect-[16/9] overflow-hidden bg-white">
                <img 
                  src="/images/heavy-equipment.jpg" 
                  alt="Heavy equipment crane battery for port and industrial operations"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="225"
                />
              </div>
              <div className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" aria-hidden="true" />
                  <h3 className="text-gray-900 font-semibold text-xs sm:text-sm uppercase tracking-wider">Heavy Equipment</h3>
                </div>
                <p className="text-gray-500 text-[10px] sm:text-xs mb-2">48V–400V | 500–1500Ah | High Current & Robustness</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                  Cranes, reach stackers, RTGs, and port equipment. Built for sustained high-current delivery under shock and vibration.
                </p>
              </div>
            </article>
          
            {/* 5. Ground Power Systems */}
            <article className="bg-gray-100 border border-gray-200 rounded-lg overflow-hidden" role="listitem">
              <div className="aspect-[16/9] overflow-hidden bg-white">
                <img 
                  src="/images/ground-power.jpg" 
                  alt="Airport ground power unit GPU battery for aircraft servicing"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="225"
                />
              </div>
              <div className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Plane className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" aria-hidden="true" />
                  <h3 className="text-gray-900 font-semibold text-xs sm:text-sm uppercase tracking-wider">Ground Power (GPU)</h3>
                </div>
                <p className="text-gray-500 text-[10px] sm:text-xs mb-2">28V–400V | 200–1500Ah | Stable Output & Continuous Duty</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                  Airport ground power units, aircraft servicing, and mobile power installations. Clean, silent operation.
                </p>
              </div>
            </article>
          
            {/* 6. Defense & Military */}
            <article className="bg-gray-100 border border-gray-200 rounded-lg overflow-hidden" role="listitem">
              <div className="aspect-[16/9] overflow-hidden bg-white">
                <img 
                  src="/images/defense-systems.jpg" 
                  alt="Defence battery systems for military ground power and tactical applications"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="225"
                />
              </div>
              <div className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" aria-hidden="true" />
                  <h3 className="text-gray-900 font-semibold text-xs sm:text-sm uppercase tracking-wider">Defense Systems</h3>
                </div>
                <p className="text-gray-500 text-[10px] sm:text-xs mb-2">24V–400V | 100–1500Ah | Reliability & Thermal Safety</p>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                  Base power, mobile command, and tactical ground power. Reliable operation in austere conditions.
                </p>
              </div>
            </article>
          </div>
        </div>

        {/* Product Tabs - Horizontal Scroll on Mobile */}
        <div className="flex gap-3 sm:gap-4 mb-12 overflow-x-auto pb-4 scrollbar-hide">
          {products.map((product, index) => {
            const Icon = product.icon;
            return (
              <button
                key={product.id}
                onClick={() => setActiveProduct(index)}
                className={`flex-shrink-0 flex flex-col items-center gap-3 p-4 sm:p-5 rounded-xl border transition-all duration-300 min-w-[140px] sm:min-w-[160px] ${
                  activeProduct === index
                    ? 'bg-emerald-500/10 border-emerald-500/50 shadow-lg shadow-emerald-500/10'
                    : 'bg-gray-900/50 border-white/10 hover:border-white/20 hover:bg-gray-800/50'
                }`}
                data-testid={`product-tab-${product.id}`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
                  activeProduct === index ? 'bg-emerald-500/20' : 'bg-white/5'
                }`}>
                  <Icon className={`w-6 h-6 ${activeProduct === index ? 'text-emerald-400' : 'text-gray-400'}`} />
                </div>
                <div className="text-center">
                  <p className={`font-semibold text-sm ${activeProduct === index ? 'text-white' : 'text-gray-300'}`}>
                    {product.name}
                  </p>
                  <p className="text-gray-500 text-xs mt-0.5">{product.tagline}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Product Detail Card */}
        <div className="max-w-4xl mx-auto">
          {/* Product Info */}
          <div className="bg-gray-900/30 border border-white/10 rounded-2xl p-8">
            {/* Category Badge */}
            {activeDetails.category && (
              <div className="inline-block bg-gray-800/80 border border-white/10 rounded px-3 py-1 mb-4">
                <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{activeDetails.category}</p>
              </div>
            )}
            <h3 className="text-3xl sm:text-4xl font-bold text-white mb-2">{activeDetails.title}</h3>
            <p className="text-emerald-400 font-medium mb-3">{activeDetails.subtitle}</p>
            
            {/* Voltage/Capacity/Requirement Summary */}
            {activeDetails.voltageRange && (
              <div className="flex flex-wrap gap-4 mb-4 py-3 border-y border-white/10">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider">Voltage Range</p>
                  <p className="text-white font-semibold">{activeDetails.voltageRange}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider">Capacity Range</p>
                  <p className="text-white font-semibold">{activeDetails.capacityRange}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider">Primary Requirement</p>
                  <p className="text-emerald-400 font-semibold text-sm">{activeDetails.primaryRequirement}</p>
                </div>
              </div>
            )}
            
            <p className="text-gray-400 text-base mb-4 leading-relaxed">{activeDetails.description}</p>
            
            {/* Application Tags */}
            {activeDetails.applications && (
              <div className="flex flex-wrap gap-2 mb-6">
                {activeDetails.applications.map((app, index) => (
                  <span key={index} className="bg-white/5 border border-white/10 text-gray-400 text-xs px-3 py-1 rounded-full">
                    {app}
                  </span>
                ))}
              </div>
            )}
            
            {/* Deployment Context */}
            {activeDetails.impact && (
              <div className="bg-gray-800/50 border border-white/10 rounded-lg p-4 mb-8">
                <p className="text-gray-300 text-sm font-medium">Deployment Context</p>
                <p className="text-gray-400 text-sm mt-1">{activeDetails.impact}</p>
              </div>
            )}

            {/* Specs Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8" role="list" aria-label="Technical specifications">
              {activeDetails.specs.map((spec, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg p-3 border border-white/5" role="listitem">
                  <p className="text-gray-500 text-xs mb-1">{spec.label}</p>
                  <p className="text-white font-semibold text-sm">{spec.value}</p>
                </div>
              ))}
            </div>

            {/* Features */}
            <div className="mb-8">
              <h4 className="text-white font-semibold mb-3">Key Features</h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {activeDetails.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden="true" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/25"
              data-testid={`product-cta-${products[activeProduct].id}`}
            >
              Request {activeDetails.title} Specifications
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
